import React, { useEffect, useState } from "react";
import { FaCheck, FaPaperPlane, FaUsers } from "react-icons/fa";

import { useNotification } from "../../../../components/Notification/NotificationManager";
import { useUser } from "../../../Home/UserContext";
import {
  getGroupsByUser,
  getGroupAdherents,
} from "../../../../services/groupServices";
import { bulkCreateAdherentEvents } from "../../../../services/eventServices";

import ModalShell from "./ModalShell";

function EventInvitationModal({ isOpen, onClose, event }) {
  const { user } = useUser();
  const { addNotification } = useNotification();

  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;

    const fetchGroups = async () => {
      if (!user?.userId) {
        addNotification("Utilisateur non connecté.", "error");
        return;
      }

      setLoadingGroups(true);

      try {
        const userGroups = await getGroupsByUser(user.userId);

        if (mounted) {
          setGroups(Array.isArray(userGroups) ? userGroups : []);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des groupes :", error);
        addNotification("Erreur lors de la récupération des groupes.", "error");
      } finally {
        if (mounted) {
          setLoadingGroups(false);
        }
      }
    };

    fetchGroups();

    return () => {
      mounted = false;
    };
  }, [isOpen, user?.userId, addNotification]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedGroups([]);
      setSending(false);
    }
  }, [isOpen]);

  if (!isOpen || !event) return null;

  const handleGroupChange = (groupId) => {
    setSelectedGroups((prevSelectedGroups) =>
      prevSelectedGroups.includes(groupId)
        ? prevSelectedGroups.filter((id) => id !== groupId)
        : [...prevSelectedGroups, groupId],
    );
  };

  const handleSend = async () => {
    if (selectedGroups.length === 0) {
      addNotification("Veuillez sélectionner au moins un groupe.", "error");
      return;
    }

    setSending(true);

    try {
      const adherentIds = [];

      for (const groupId of selectedGroups) {
        const response = await getGroupAdherents(groupId);
        const groupAdherents = response?.adherents || response || [];

        groupAdherents.forEach((adherent) => {
          if (adherent?.id && !adherentIds.includes(adherent.id)) {
            adherentIds.push(adherent.id);
          }
        });
      }

      if (adherentIds.length === 0) {
        addNotification(
          "Aucun adhérent trouvé dans les groupes sélectionnés.",
          "error",
        );
        return;
      }

      await bulkCreateAdherentEvents({
        event_id: event.id,
        adherent_ids: adherentIds,
      });

      addNotification("Les invitations ont été envoyées avec succès !", "success");
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'envoi des invitations :", error);
      addNotification("Erreur lors de l'envoi des invitations.", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalShell
      title="Envoyer une invitation"
      eyebrow={event.titre || "Événement"}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      <div className="mb-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
            <FaUsers />
          </div>

          <div>
            <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
              Sélection des groupes
            </h3>

            <p className="mt-1 text-sm font-medium leading-6 text-slate-600 dark:text-white/55">
              Les adhérents des groupes sélectionnés recevront une invitation
              pour cet événement.
            </p>
          </div>
        </div>
      </div>

      {loadingGroups ? (
        <div className="space-y-3">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-14 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/10"
            />
          ))}
        </div>
      ) : groups.length > 0 ? (
        <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
          {groups.map((group) => {
            const isSelected = selectedGroups.includes(group.id);

            return (
              <button
                key={group.id}
                type="button"
                onClick={() => handleGroupChange(group.id)}
                className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition ${
                  isSelected
                    ? "border-orange-300 bg-orange-50 shadow-lg shadow-orange-500/10 dark:border-orange-400/30 dark:bg-orange-500/10"
                    : "border-slate-200 bg-white hover:bg-slate-50 dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/10"
                }`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                    isSelected
                      ? "border-orange-500 bg-orange-500 text-white"
                      : "border-slate-300 bg-white dark:border-white/20 dark:bg-white/10"
                  }`}
                >
                  {isSelected && <FaCheck className="text-[10px]" />}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block font-bold text-slate-950 dark:text-white">
                    {group.nom || "Groupe sans nom"}
                  </span>

                  {group.description && (
                    <span className="mt-1 block text-sm font-medium text-slate-500 dark:text-white/50">
                      {group.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 text-center dark:border-white/10 dark:bg-white/[0.05]">
          <p className="text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
            Aucun groupe disponible. Créez des groupes pour envoyer des
            invitations.
          </p>
        </div>
      )}

      <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 dark:border-white/10 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={sending}
          className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/10"
        >
          Annuler
        </button>

        <button
          type="button"
          onClick={handleSend}
          disabled={sending || selectedGroups.length === 0}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FaPaperPlane />
          {sending ? "Envoi..." : "Envoyer les invitations"}
        </button>
      </div>
    </ModalShell>
  );
}

export default EventInvitationModal;